import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Megaphone, Calendar, DollarSign, LogOut, CheckCircle, Eye, EyeOff, Trophy, Users, ChevronDown, ChevronRight, UserCircle, ShieldCheck, UserCog, CreditCard, Trash2, Menu, X, Upload, UserMinus } from 'lucide-react';

function AdminPaymentNoteWidget({ payment, kind, onSaveNote, onUploadReceipt }) {
  const [note, setNote] = useState(payment.admin_note || '');
  const initial = payment.admin_note || '';
  return (
    <div className="pt-2 border-t border-slate-100 space-y-2 w-full" data-testid={`admin-note-widget-${payment.id}`}>
      <p className="text-xs font-semibold text-slate-600">Cash Note & Receipt Upload</p>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-2 w-full">
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder='Cash note (e.g., "Paid via cash")'
          className="flex-1 min-w-0 text-xs border border-slate-300 rounded-md px-2 py-2 sm:py-1.5 focus:outline-none focus:ring-2 focus:ring-secondary/40"
          data-testid={`admin-note-input-${payment.id}`}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-9 sm:h-8 text-xs w-full sm:w-auto shrink-0"
          disabled={note === initial}
          onClick={() => onSaveNote(payment, kind, note)}
          data-testid={`admin-note-save-${payment.id}`}
        >
          Save Note
        </Button>
      </div>
      <label
        className="flex items-center justify-center sm:justify-start gap-2 text-xs font-medium text-secondary border border-secondary/40 hover:bg-secondary/10 rounded-md px-3 py-2 sm:py-1.5 cursor-pointer w-full sm:w-auto"
        data-testid={`admin-upload-label-${payment.id}`}
      >
        <Upload className="h-4 w-4 shrink-0" />
        <span className="truncate">Upload receipt on behalf of parent</span>
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onUploadReceipt(payment, kind, e.target.files[0])}
          data-testid={`admin-upload-input-${payment.id}`}
        />
      </label>
      {payment.uploaded_by_admin && payment.gcash_receipt_path && (
        <p className="text-xs text-emerald-700">✓ Receipt uploaded by admin</p>
      )}
    </div>
  );
}
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('announcements');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const selectTab = (id) => {
    setActiveTab(id);
    setSidebarOpen(false);
  };
  const [announcements, setAnnouncements] = useState([]);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [trainingPayments, setTrainingPayments] = useState([]);
  const [tournamentPayments, setTournamentPayments] = useState([]);
  const [playerRoster, setPlayerRoster] = useState([]);
  const [users, setUsers] = useState([]);
  const [paymentChannels, setPaymentChannels] = useState([]);
  const [showAddChannel, setShowAddChannel] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [channelForm, setChannelForm] = useState({ name: '', account_name: '', number: '', instructions: '', is_active: true });
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showPast, setShowPast] = useState({ sessions: false, tournaments: false, trainingPayments: false, tournamentPayments: false });
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const [showAddTournament, setShowAddTournament] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [editingTournament, setEditingTournament] = useState(null);
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [tournamentRoster, setTournamentRoster] = useState([]);
  const [showSessionRosterModal, setShowSessionRosterModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionRoster, setSessionRoster] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', content: '', image_url: '' });
  const [sessionForm, setSessionForm] = useState({ title: '', location: '', date_time: '', fee_type: 'standard', notes: '' });
  const [tournamentForm, setTournamentForm] = useState({
    title: '', category: '', participant_limit: 20, location: '', start_date: '', registration_deadline: '', fee: '', description: '', notes: ''
  });
  const [tournamentBanner, setTournamentBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allPlayersFlat, setAllPlayersFlat] = useState([]);
  const [manualDialog, setManualDialog] = useState({ open: false, type: null, target: null });
  const [manualPlayerId, setManualPlayerId] = useState('');

  // Split an array into upcoming (>= start of today) and past (< start of today).
  // upcoming is sorted ascending (nearest first); past is sorted descending (most recent first).
  const partitionByDate = (items, getDate) => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const upcoming = [];
    const past = [];
    for (const item of items || []) {
      const raw = getDate(item);
      if (!raw) { upcoming.push(item); continue; }
      const d = new Date(raw);
      if (isNaN(d.getTime())) { upcoming.push(item); continue; }
      (d >= start ? upcoming : past).push(item);
    }
    const toTs = (item) => {
      const raw = getDate(item);
      const d = raw ? new Date(raw) : null;
      return d && !isNaN(d.getTime()) ? d.getTime() : 0;
    };
    upcoming.sort((a, b) => toTs(a) - toTs(b));
    past.sort((a, b) => toTs(b) - toTs(a));
    return { upcoming, past };
  };

  const togglePast = (key) => setShowPast(prev => ({ ...prev, [key]: !prev[key] }));

  const sessionParts = partitionByDate(trainingSessions, s => s.date_time);
  const tournamentParts = partitionByDate(tournaments, t => t.start_date);
  const trainingPaymentParts = partitionByDate(trainingPayments, g => g.session_date);
  const tournamentPaymentParts = partitionByDate(tournamentPayments, g => g.start_date);

  const fetchData = useCallback(async () => {
    try {
      if (activeTab === 'announcements') {
        const res = await axios.get(`${API}/admin/announcements`, { withCredentials: true });
        setAnnouncements(res.data);
      } else if (activeTab === 'sessions') {
        const res = await axios.get(`${API}/admin/training-sessions`, { withCredentials: true });
        setTrainingSessions(res.data);
      } else if (activeTab === 'tournaments') {
        const res = await axios.get(`${API}/admin/tournaments`, { withCredentials: true });
        setTournaments(res.data);
      } else if (activeTab === 'training-payments') {
        const res = await axios.get(`${API}/admin/training-payments`, { withCredentials: true });
        setTrainingPayments(res.data);
      } else if (activeTab === 'tournament-payments') {
        const res = await axios.get(`${API}/admin/tournament-payments`, { withCredentials: true });
        setTournamentPayments(res.data);
      } else if (activeTab === 'roster') {
        const res = await axios.get(`${API}/admin/players`, { withCredentials: true });
        setPlayerRoster(res.data);
      } else if (activeTab === 'users') {
        const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
        setUsers(res.data);
      } else if (activeTab === 'payment-channels') {
        const res = await axios.get(`${API}/admin/payment-channels`, { withCredentials: true });
        setPaymentChannels(res.data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', error);
      }
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddAnnouncement = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingAnnouncement) {
        await axios.put(`${API}/admin/announcements/${editingAnnouncement}`, announcementForm, { withCredentials: true });
        toast.success('Announcement updated!');
      } else {
        await axios.post(`${API}/admin/announcements`, announcementForm, { withCredentials: true });
        toast.success('Announcement created!');
      }
      setShowAddAnnouncement(false);
      setEditingAnnouncement(null);
      setAnnouncementForm({ title: '', content: '', image_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save announcement');
    } finally {
      setLoading(false);
    }
  };

  const openEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement.id);
    setAnnouncementForm({
      title: announcement.title,
      content: announcement.content,
      image_url: announcement.image_url || ''
    });
    setShowAddAnnouncement(true);
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, { withCredentials: true });
      toast.success('Announcement deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleAddSession = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingSession) {
        await axios.put(`${API}/admin/training-sessions/${editingSession}`, sessionForm, { withCredentials: true });
        toast.success('Training session updated!');
      } else {
        await axios.post(`${API}/admin/training-sessions`, sessionForm, { withCredentials: true });
        toast.success('Training session created!');
      }
      setShowAddSession(false);
      setEditingSession(null);
      setSessionForm({ title: '', location: '', date_time: '', fee_type: 'standard', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save session');
    } finally {
      setLoading(false);
    }
  };

  const openEditSession = (session) => {
    setEditingSession(session.id);
    setSessionForm({
      title: session.title,
      location: session.location,
      date_time: session.date_time,
      fee_type: session.fee_type,
      notes: session.notes || ''
    });
    setShowAddSession(true);
  };

  const handleDeleteSession = async (id) => {
    try {
      await axios.delete(`${API}/admin/training-sessions/${id}`, { withCredentials: true });
      toast.success('Session deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const handleAddTournament = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...tournamentForm,
        participant_limit: parseInt(tournamentForm.participant_limit),
        fee: parseFloat(tournamentForm.fee),
      };
      let tournamentId = editingTournament;
      if (editingTournament) {
        await axios.put(`${API}/admin/tournaments/${editingTournament}`, payload, { withCredentials: true });
        toast.success('Tournament updated!');
      } else {
        const res = await axios.post(`${API}/admin/tournaments`, payload, { withCredentials: true });
        tournamentId = res.data.id;
        toast.success('Tournament created!');
      }
      // Upload banner if provided
      if (tournamentBanner && tournamentId) {
        try {
          const bannerData = new FormData();
          bannerData.append('file', tournamentBanner);
          await axios.post(`${API}/admin/tournaments/${tournamentId}/upload-banner`, bannerData, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Banner uploaded!');
        } catch (bannerErr) {
          toast.error('Tournament saved but banner upload failed. Try again from Edit.');
        }
      }
      setShowAddTournament(false);
      setEditingTournament(null);
      setTournamentForm({ title: '', category: '', participant_limit: 20, location: '', start_date: '', registration_deadline: '', fee: '', description: '', notes: '' });
      setTournamentBanner(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save tournament');
    } finally {
      setLoading(false);
    }
  };

  const openEditTournament = (tournament) => {
    setEditingTournament(tournament.id);
    setTournamentForm({
      title: tournament.title,
      category: tournament.category,
      participant_limit: tournament.participant_limit,
      location: tournament.location || '',
      start_date: tournament.start_date,
      registration_deadline: tournament.registration_deadline,
      fee: tournament.fee !== undefined && tournament.fee !== null ? String(tournament.fee) : '',
      description: tournament.description || '',
      notes: tournament.notes || ''
    });
    setShowAddTournament(true);
  };

  const handleDialogClose = (dialogSetter, editingSetter, formSetter, defaultForm) => (open) => {
    if (!open) {
      editingSetter(null);
      formSetter(defaultForm);
    }
    dialogSetter(open);
  };

  const handleDeleteTournament = async (id) => {
    try {
      await axios.delete(`${API}/admin/tournaments/${id}`, { withCredentials: true });
      toast.success('Tournament deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete tournament');
    }
  };

  const viewRoster = async (tournament) => {
    try {
      const res = await axios.get(`${API}/admin/tournaments/${tournament.id}/registrations`, { withCredentials: true });
      setTournamentRoster(res.data);
      setSelectedTournament(tournament);
      setShowRosterModal(true);
    } catch (error) {
      toast.error('Failed to load roster');
    }
  };

  const handleVerifyPayment = async (paymentId, paymentType) => {
    try {
      await axios.post(`${API}/admin/payments/verify`, { payment_id: paymentId, payment_type: paymentType }, { withCredentials: true });
      toast.success('Payment verified!');
      fetchData();
    } catch (error) {
      toast.error('Failed to verify payment');
    }
  };

  const handleUpdateAttendance = async (checkIn, value) => {
    // value is "" | "Present" | "No Show"; map "" -> null on backend
    const attendance = value === '' ? null : value;
    if ((checkIn.attendance || '') === (attendance || '')) return;
    try {
      await axios.patch(
        `${API}/admin/check-ins/${checkIn.id}/attendance`,
        { attendance },
        { withCredentials: true }
      );
      toast.success(attendance ? `Marked ${checkIn.player_name} as ${attendance}` : 'Attendance cleared');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update attendance');
    }
  };

  const handleAdminSaveNote = async (payment, kind, note) => {
    const path = kind === 'training' ? 'check-ins' : 'tournament-registrations';
    const fd = new FormData();
    fd.append('note', note ?? '');
    try {
      await axios.post(`${API}/admin/${path}/${payment.id}/payment-note`, fd, { withCredentials: true });
      toast.success('Note saved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save note');
    }
  };

  const handleAdminUploadReceipt = async (payment, kind, file) => {
    if (!file) return;
    const path = kind === 'training' ? 'check-ins' : 'tournament-registrations';
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/admin/${path}/${payment.id}/payment-note`, fd, { withCredentials: true });
      toast.success('Receipt uploaded on behalf of parent');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload receipt');
    }
  };

  const handleWithdrawRegistrant = async ({ id, kind, player_name, fee_amount }) => {
    const eventLabel = kind === 'training' ? 'training session' : 'tournament';
    const feeText = fee_amount != null ? `₱${Number(fee_amount).toLocaleString()} ` : '';
    const ok = window.confirm(
      `Withdraw ${player_name} from this ${eventLabel}?\n\n` +
      `This clears the ${feeText}fee from the parent's outstanding balance and removes the player from the roster. ` +
      `No refund is issued for amounts already paid.`
    );
    if (!ok) return;
    const path = kind === 'training' ? 'check-ins' : 'tournament-registrations';
    try {
      await axios.delete(`${API}/admin/${path}/${id}`, { withCredentials: true });
      toast.success(`${player_name} withdrawn`);
      fetchData();
      // Refresh open roster modals if applicable
      if (kind === 'training' && selectedSession) {
        try {
          const res = await axios.get(`${API}/admin/training-sessions/${selectedSession.id}/roster`, { withCredentials: true });
          setSessionRoster(res.data);
        } catch (_) {}
      }
      if (kind === 'tournament' && selectedTournament) {
        try {
          const res = await axios.get(`${API}/admin/tournaments/${selectedTournament.id}/registrations`, { withCredentials: true });
          setTournamentRoster(res.data);
        } catch (_) {}
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw registrant');
    }
  };

  const loadAllPlayersFlat = async () => {
    try {
      const res = await axios.get(`${API}/admin/players`, { withCredentials: true });
      const flat = res.data.flatMap(g => g.players.map(p => ({ ...p, group_label: g.category })));
      setAllPlayersFlat(flat);
    } catch (e) {
      toast.error('Failed to load players list');
    }
  };

  const openManualDialog = async (type, target) => {
    await loadAllPlayersFlat();
    setManualPlayerId('');
    setManualDialog({ open: true, type, target });
  };

  const submitManualAction = async () => {
    if (!manualPlayerId) {
      toast.error('Please choose a player');
      return;
    }
    const { type, target } = manualDialog;
    try {
      if (type === 'training') {
        await axios.post(`${API}/admin/training-sessions/${target.id}/manual-checkin`, { player_id: manualPlayerId }, { withCredentials: true });
        toast.success('Player checked in — unpaid balance added to parent');
      } else {
        await axios.post(`${API}/admin/tournaments/${target.id}/manual-register`, { player_id: manualPlayerId }, { withCredentials: true });
        toast.success('Player registered — unpaid balance added to parent');
      }
      setManualDialog({ open: false, type: null, target: null });
      setManualPlayerId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Manual action failed');
    }
  };

  const viewReceipt = (receiptPath) => {
    setSelectedReceipt(`${process.env.REACT_APP_BACKEND_URL}/api/files/${receiptPath}`);
    setShowReceiptModal(true);
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDeletePlayer = async (player) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove ${player.name}? This will also delete all their check-ins and tournament registrations. This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/players/${player.id}`, { withCredentials: true });
      toast.success(`${player.name} removed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete player');
    }
  };

  const handleUpdateMembership = async (player, newStatus) => {
    if (!newStatus || newStatus === player.membership_status) return;
    try {
      await axios.patch(
        `${API}/admin/players/${player.id}/membership`,
        { membership_status: newStatus },
        { withCredentials: true }
      );
      toast.success(`${player.name}'s membership set to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update membership');
    }
  };

  const handleDeleteUser = async (targetUser) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove ${targetUser.name} (${targetUser.email})? This will also delete their players, check-ins, and tournament registrations. This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/users/${targetUser.id}`, { withCredentials: true });
      toast.success(`${targetUser.name} removed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleRole = async (targetUser) => {
    const newRole = targetUser.role === 'admin' ? 'member' : 'admin';
    try {
      await axios.patch(`${API}/admin/users/${targetUser.id}/role`, { role: newRole }, { withCredentials: true });
      toast.success(`${targetUser.name} is now ${newRole === 'admin' ? 'an Admin' : 'a Member'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const viewSessionRoster = async (session) => {
    try {
      const res = await axios.get(`${API}/admin/training-sessions/${session.id}/roster`, { withCredentials: true });
      setSessionRoster(res.data);
      setSelectedSession(session);
      setShowSessionRosterModal(true);
    } catch (error) {
      toast.error('Failed to load roster');
    }
  };

  const handleSaveChannel = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingChannel) {
        await axios.put(`${API}/admin/payment-channels/${editingChannel}`, channelForm, { withCredentials: true });
        toast.success('Payment channel updated!');
      } else {
        await axios.post(`${API}/admin/payment-channels`, channelForm, { withCredentials: true });
        toast.success('Payment channel created!');
      }
      setShowAddChannel(false);
      setEditingChannel(null);
      setChannelForm({ name: '', account_name: '', number: '', instructions: '', is_active: true });
      fetchData();
    } catch (error) {
      toast.error('Failed to save channel');
    } finally {
      setLoading(false);
    }
  };

  const openEditChannel = (channel) => {
    setEditingChannel(channel.id);
    setChannelForm({
      name: channel.name,
      account_name: channel.account_name,
      number: channel.number,
      instructions: channel.instructions || '',
      is_active: channel.is_active
    });
    setShowAddChannel(true);
  };

  const handleDeleteChannel = async (id) => {
    try {
      await axios.delete(`${API}/admin/payment-channels/${id}`, { withCredentials: true });
      toast.success('Channel deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete channel');
    }
  };

  const tabs = [
    { id: 'announcements', label: 'Announcements', icon: Megaphone },
    { id: 'sessions', label: 'Training Sessions', icon: Calendar },
    { id: 'tournaments', label: 'Tournament Hub', icon: Trophy },
    { id: 'roster', label: 'Player Roster', icon: UserCircle },
    { id: 'users', label: 'User Management', icon: UserCog },
    { id: 'payment-channels', label: 'Payment Channels', icon: CreditCard },
    { id: 'training-payments', label: 'Training Payments', icon: DollarSign },
    { id: 'tournament-payments', label: 'Tournament Payments', icon: DollarSign },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] lg:flex">
      {/* Mobile hamburger toggle */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="lg:hidden fixed top-3 left-3 z-30 inline-flex items-center justify-center w-11 h-11 rounded-lg bg-primary text-white shadow-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-accent"
        aria-label="Open navigation menu"
        data-testid="admin-nav-open"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Backdrop for mobile drawer */}
      {sidebarOpen && (
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          aria-label="Close navigation menu"
          data-testid="admin-nav-backdrop"
        />
      )}

      <aside
        className={`bg-primary text-white w-64 flex-shrink-0 min-h-screen fixed lg:sticky top-0 left-0 z-50 transform transition-transform duration-200 ease-out lg:transform-none lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:h-screen lg:flex lg:flex-col`}
        data-testid="admin-sidebar"
      >
        {/* Close button visible only on mobile drawer */}
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-md text-white/80 hover:bg-white/10"
          aria-label="Close navigation menu"
          data-testid="admin-nav-close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="p-6">
          <img
            src="https://customer-assets-jt897jd0.emergentagent.net/job_family-athletics/artifacts/uy0gl72p_New%20Offcial%20Logo.jpg"
            alt="Dolphins FC"
            className="h-16 mx-auto mb-4"
          />
          <h1 className="font-barlow-condensed text-xl font-bold text-center" data-testid="admin-dashboard-title">Admin Dashboard</h1>
        </div>
        <nav className="mt-4 flex-1 overflow-y-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => selectTab(tab.id)}
                className={`w-full flex items-center gap-3 px-6 py-3 transition-colors text-left ${
                  activeTab === tab.id ? 'bg-white/20 border-l-4 border-accent' : 'hover:bg-white/10'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium text-sm">{tab.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="p-6 lg:mt-auto">
          <div className="mb-4 text-sm">
            <p className="text-white/60">Logged in as</p>
            <p className="font-semibold truncate">{user?.email}</p>
          </div>
          <Button
            onClick={() => navigate('/member')}
            variant="ghost"
            className="w-full text-white hover:bg-white/10 mb-2"
            data-testid="view-as-member-button"
          >
            <Eye className="h-4 w-4 mr-2" />
            View as Member
          </Button>
          <Button
            onClick={logout}
            variant="ghost"
            className="w-full text-white hover:bg-white/10"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-4 pt-16 sm:p-6 sm:pt-16 lg:p-8 lg:pt-8 overflow-auto">
        {activeTab === 'announcements' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-barlow-condensed text-3xl font-bold text-primary tracking-tight">Manage Announcements</h2>
              <Dialog open={showAddAnnouncement} onOpenChange={handleDialogClose(setShowAddAnnouncement, setEditingAnnouncement, setAnnouncementForm, { title: '', content: '', image_url: '' })}>
                <DialogTrigger asChild>
                  <Button className="bg-secondary hover:bg-secondary/90 text-white" data-testid="add-announcement-button">
                    Create Announcement
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddAnnouncement} className="space-y-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={announcementForm.title}
                        onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                        required
                        data-testid="announcement-title-input"
                      />
                    </div>
                    <div>
                      <Label>Content</Label>
                      <Textarea
                        value={announcementForm.content}
                        onChange={(e) => setAnnouncementForm({ ...announcementForm, content: e.target.value })}
                        required
                        rows={4}
                        data-testid="announcement-content-input"
                      />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-primary text-white hover:bg-primary/90" data-testid="announcement-submit-button">
                      {loading ? 'Saving...' : editingAnnouncement ? 'Update Announcement' : 'Create Announcement'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-4">
              {announcements.map(announcement => (
                <Card key={announcement.id} className="p-6 border border-slate-200" data-testid={`announcement-card-${announcement.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-barlow-condensed text-xl font-bold text-primary mb-2">{announcement.title}</h3>
                      <p className="text-slate-600">{announcement.content}</p>
                      <p className="text-xs text-slate-400 mt-2">{new Date(announcement.created_at).toLocaleDateString()}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditAnnouncement(announcement)}
                      data-testid={`edit-announcement-${announcement.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteAnnouncement(announcement.id)}
                      data-testid={`delete-announcement-${announcement.id}`}
                    >
                      Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'sessions' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-barlow-condensed text-3xl font-bold text-primary tracking-tight">Manage Training Sessions</h2>
              <Dialog open={showAddSession} onOpenChange={handleDialogClose(setShowAddSession, setEditingSession, setSessionForm, { title: '', location: '', date_time: '', fee_type: 'standard', notes: '' })}>
                <DialogTrigger asChild>
                  <Button className="bg-secondary hover:bg-secondary/90 text-white" data-testid="add-session-button">
                    Create Session
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingSession ? 'Edit Training Session' : 'Create Training Session'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddSession} className="space-y-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={sessionForm.title}
                        onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
                        required
                        data-testid="session-title-input"
                      />
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={sessionForm.location}
                        onChange={(e) => setSessionForm({ ...sessionForm, location: e.target.value })}
                        required
                        data-testid="session-location-input"
                      />
                    </div>
                    <div>
                      <Label>Date &amp; Time</Label>
                      <Input
                        type="datetime-local"
                        value={sessionForm.date_time}
                        onChange={(e) => setSessionForm({ ...sessionForm, date_time: e.target.value })}
                        required
                        data-testid="session-datetime-input"
                      />
                    </div>
                    <div>
                      <Label>Fee Type</Label>
                      <Select value={sessionForm.fee_type} onValueChange={(value) => setSessionForm({ ...sessionForm, fee_type: value })}>
                        <SelectTrigger data-testid="session-feetype-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standard">Standard (₱100)</SelectItem>
                          <SelectItem value="special">Special (₱250)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Additional Notes (Optional)</Label>
                      <Textarea
                        value={sessionForm.notes}
                        onChange={(e) => setSessionForm({ ...sessionForm, notes: e.target.value })}
                        placeholder="e.g., Bring water bottle and shin guards"
                        rows={3}
                        data-testid="session-notes-input"
                      />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-primary text-white hover:bg-primary/90" data-testid="session-submit-button">
                      {loading ? 'Saving...' : editingSession ? 'Update Session' : 'Create Session'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {trainingSessions.length === 0 ? (
              <p className="text-slate-500">No training sessions yet.</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-slate-600">
                    {sessionParts.upcoming.length} upcoming
                    {sessionParts.past.length > 0 && <span className="text-slate-400"> • {sessionParts.past.length} past</span>}
                  </p>
                  {sessionParts.past.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => togglePast('sessions')}
                      data-testid="toggle-past-sessions"
                    >
                      {showPast.sessions ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      {showPast.sessions ? 'Hide Past' : `Show Past (${sessionParts.past.length})`}
                    </Button>
                  )}
                </div>
                <div className="space-y-4">
                  {(showPast.sessions ? [...sessionParts.upcoming, ...sessionParts.past] : sessionParts.upcoming).map(session => {
                    const isPast = new Date(session.date_time) < new Date(new Date().setHours(0,0,0,0));
                    return (
                <Card key={session.id} className={`p-4 sm:p-6 border border-slate-200 ${isPast ? 'opacity-70 bg-slate-50' : ''}`} data-testid={`session-card-${session.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-barlow-condensed text-xl font-bold text-primary">{session.title}</h3>
                        {isPast && <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-semibold">Past</span>}
                      </div>
                      <p className="text-sm text-slate-600"><span className="font-medium">Location:</span> {session.location}</p>
                      <p className="text-sm text-slate-600"><span className="font-medium">Date:</span> {new Date(session.date_time).toLocaleString()}</p>
                      <p className="text-sm text-slate-600"><span className="font-medium">Fee:</span> {session.fee_type === 'standard' ? '₱100' : '₱250'}</p>
                    </div>
                    <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full md:w-auto"
                        onClick={() => openManualDialog('training', session)}
                        data-testid={`manual-checkin-btn-${session.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Manual Check-In
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full md:w-auto"
                        onClick={() => viewSessionRoster(session)}
                        data-testid={`view-session-roster-${session.id}`}
                      >
                        <Users className="h-4 w-4 mr-1" />
                        View Roster
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full md:w-auto"
                        onClick={() => openEditSession(session)}
                        data-testid={`edit-session-${session.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="w-full md:w-auto"
                        onClick={() => handleDeleteSession(session.id)}
                        data-testid={`delete-session-${session.id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'tournaments' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-barlow-condensed text-3xl font-bold text-primary tracking-tight">Tournament Hub</h2>
              <Dialog open={showAddTournament} onOpenChange={handleDialogClose(setShowAddTournament, setEditingTournament, setTournamentForm, { title: '', category: '', participant_limit: 20, location: '', start_date: '', registration_deadline: '', description: '', notes: '' })}>
                <DialogTrigger asChild>
                  <Button className="bg-secondary hover:bg-secondary/90 text-white" data-testid="add-tournament-button">
                    <Trophy className="h-4 w-4 mr-2" />
                    Create Tournament
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 bg-white sticky top-0 z-10 flex-shrink-0">
                    <DialogTitle>{editingTournament ? 'Edit Tournament' : 'Create Tournament'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddTournament} className="space-y-4 overflow-y-auto px-6 py-6 flex-1">
                    <div>
                      <Label>Tournament Title</Label>
                      <Input
                        value={tournamentForm.title}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, title: e.target.value })}
                        required
                        data-testid="tournament-title-input"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Input
                        value={tournamentForm.category}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, category: e.target.value })}
                        required
                        placeholder="e.g., U6, U7, U8, U10, U11, U12, Open"
                        data-testid="tournament-category-input"
                      />
                    </div>
                    <div>
                      <Label>Participant Limit</Label>
                      <Input
                        type="number"
                        value={tournamentForm.participant_limit}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, participant_limit: e.target.value })}
                        required
                        min="1"
                        data-testid="tournament-limit-input"
                      />
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={tournamentForm.location}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, location: e.target.value })}
                        required
                        placeholder="e.g., PCU Sports Complex, Field A"
                        data-testid="tournament-location-input"
                      />
                    </div>
                    <div>
                      <Label>Start Date &amp; Time</Label>
                      <Input
                        type="datetime-local"
                        value={tournamentForm.start_date}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, start_date: e.target.value })}
                        required
                        data-testid="tournament-start-input"
                      />
                    </div>
                    <div>
                      <Label>Registration Deadline</Label>
                      <Input
                        type="datetime-local"
                        value={tournamentForm.registration_deadline}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, registration_deadline: e.target.value })}
                        required
                        data-testid="tournament-deadline-input"
                      />
                    </div>
                    <div>
                      <Label>Registration Fee (₱)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={tournamentForm.fee}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, fee: e.target.value })}
                        required
                        placeholder="e.g., 250"
                        data-testid="tournament-fee-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Amount each player pays to join this tournament.</p>
                    </div>
                    <div>
                      <Label>Description (Optional)</Label>
                      <Textarea
                        value={tournamentForm.description}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, description: e.target.value })}
                        rows={3}
                        data-testid="tournament-description-input"
                      />
                    </div>
                    <div>
                      <Label>Additional Notes (Optional)</Label>
                      <Textarea
                        value={tournamentForm.notes}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, notes: e.target.value })}
                        placeholder="e.g., Team must arrive 30 mins early, bring jerseys"
                        rows={3}
                        data-testid="tournament-notes-input"
                      />
                    </div>
                    <div>
                      <Label>Tournament Banner (Portrait Image)</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setTournamentBanner(e.target.files[0])}
                        data-testid="tournament-banner-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Recommended: portrait/Facebook-post style (4:5 or 3:4). Image will be embedded in the member registration card.</p>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-primary text-white hover:bg-primary/90" data-testid="tournament-submit-button">
                      {loading ? 'Saving...' : editingTournament ? 'Update Tournament' : 'Create Tournament'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {tournaments.length === 0 ? (
              <p className="text-slate-500">No tournaments created yet.</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-slate-600">
                    {tournamentParts.upcoming.length} upcoming
                    {tournamentParts.past.length > 0 && <span className="text-slate-400"> • {tournamentParts.past.length} past</span>}
                  </p>
                  {tournamentParts.past.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => togglePast('tournaments')}
                      data-testid="toggle-past-tournaments"
                    >
                      {showPast.tournaments ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      {showPast.tournaments ? 'Hide Past' : `Show Past (${tournamentParts.past.length})`}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(showPast.tournaments ? [...tournamentParts.upcoming, ...tournamentParts.past] : tournamentParts.upcoming).map(tournament => {
                    const isPast = new Date(tournament.start_date) < new Date(new Date().setHours(0,0,0,0));
                    return (
                  <Card key={tournament.id} className={`p-6 border border-slate-200 ${isPast ? 'opacity-70 bg-slate-50' : ''}`} data-testid={`tournament-card-${tournament.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-barlow-condensed text-xl font-bold text-primary">{tournament.title}</h3>
                          {isPast && <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-semibold">Past</span>}
                        </div>
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-secondary/20 text-secondary">
                          {tournament.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Users className="h-4 w-4" />
                        <span className="font-semibold">{tournament.registration_count}/{tournament.participant_limit}</span>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-slate-600 mb-4">
                      <p><span className="font-medium">Location:</span> {tournament.location || 'TBA'}</p>
                      <p><span className="font-medium">Start:</span> {new Date(tournament.start_date).toLocaleString()}</p>
                      <p><span className="font-medium">Deadline:</span> {new Date(tournament.registration_deadline).toLocaleDateString()}</p>
                      {tournament.description && <p className="text-slate-500 mt-2">{tournament.description}</p>}
                      {tournament.notes && (
                        <div className="mt-3 p-2 bg-accent/10 border-l-2 border-accent rounded">
                          <p className="text-xs font-semibold text-accent uppercase tracking-wide">Notes</p>
                          <p className="text-sm text-slate-700">{tournament.notes}</p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => openManualDialog('tournament', tournament)}
                        data-testid={`manual-register-btn-${tournament.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Manual Register
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewRoster(tournament)}
                        className="w-full sm:w-auto sm:flex-1"
                        data-testid={`view-roster-${tournament.id}`}
                      >
                        <Users className="h-4 w-4 mr-1" />
                        View Roster
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => openEditTournament(tournament)}
                        data-testid={`edit-tournament-${tournament.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="w-full sm:w-auto"
                        onClick={() => handleDeleteTournament(tournament.id)}
                        data-testid={`delete-tournament-${tournament.id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </Card>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <h2 className="font-barlow-condensed text-3xl font-bold text-primary tracking-tight mb-2">User Management</h2>
            <p className="text-slate-600 mb-6">Grant or revoke Admin access. Only Admins can manage roles.</p>
            <Card className="border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Players</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => {
                    const isSelf = u.id === user?.id;
                    return (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-sm">{u.email}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${u.role === 'admin' ? 'bg-accent/20 text-accent' : 'bg-slate-100 text-slate-700'}`}>
                            {u.role === 'admin' && <ShieldCheck className="h-3 w-3" />}
                            {u.role}
                          </span>
                        </TableCell>
                        <TableCell>{u.player_count}</TableCell>
                        <TableCell className="text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</TableCell>
                        <TableCell className="text-right">
                          {isSelf ? (
                            <span className="text-xs text-slate-400 italic">You</span>
                          ) : (
                            <div className="flex justify-end gap-2">
                              {u.role === 'admin' ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleToggleRole(u)}
                                  data-testid={`revoke-admin-${u.id}`}
                                  className="border-red-300 text-red-700 hover:bg-red-50"
                                >
                                  Revoke Admin
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => handleToggleRole(u)}
                                  data-testid={`grant-admin-${u.id}`}
                                  className="bg-primary text-white hover:bg-primary/90"
                                >
                                  <ShieldCheck className="h-4 w-4 mr-1" />
                                  Grant Admin
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteUser(u)}
                                data-testid={`delete-user-${u.id}`}
                                className="border-red-300 text-red-700 hover:bg-red-50"
                                aria-label={`Delete user ${u.name}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}


        {activeTab === 'payment-channels' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-barlow-condensed text-3xl font-bold text-primary tracking-tight">Payment Channels</h2>
                <p className="text-slate-600 mt-1">Manage payment options members can use to pay fees.</p>
              </div>
              <Dialog open={showAddChannel} onOpenChange={(open) => {
                if (!open) { setEditingChannel(null); setChannelForm({ name: '', account_name: '', number: '', instructions: '', is_active: true }); }
                setShowAddChannel(open);
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-secondary hover:bg-secondary/90 text-white" data-testid="add-channel-button">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Add Channel
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingChannel ? 'Edit Payment Channel' : 'Add Payment Channel'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveChannel} className="space-y-4">
                    <div>
                      <Label>Channel Name</Label>
                      <Input value={channelForm.name} onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })} required placeholder="e.g., GCash, PayMaya, BPI" data-testid="channel-name-input" />
                    </div>
                    <div>
                      <Label>Account Name</Label>
                      <Input value={channelForm.account_name} onChange={(e) => setChannelForm({ ...channelForm, account_name: e.target.value })} required placeholder="e.g., Dolphins FC PCU" data-testid="channel-account-input" />
                    </div>
                    <div>
                      <Label>Account Number</Label>
                      <Input value={channelForm.number} onChange={(e) => setChannelForm({ ...channelForm, number: e.target.value })} required placeholder="e.g., 0917-123-4567" data-testid="channel-number-input" />
                    </div>
                    <div>
                      <Label>Instructions (Optional)</Label>
                      <Textarea value={channelForm.instructions} onChange={(e) => setChannelForm({ ...channelForm, instructions: e.target.value })} rows={3} placeholder="Any special instructions for members" data-testid="channel-instructions-input" />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="channel-active"
                        checked={channelForm.is_active}
                        onChange={(e) => setChannelForm({ ...channelForm, is_active: e.target.checked })}
                        className="h-4 w-4"
                        data-testid="channel-active-input"
                      />
                      <Label htmlFor="channel-active" className="cursor-pointer">Active (visible to members)</Label>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-primary text-white hover:bg-primary/90" data-testid="channel-submit-button">
                      {loading ? 'Saving...' : editingChannel ? 'Update Channel' : 'Create Channel'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {paymentChannels.length === 0 ? (
              <p className="text-slate-500">No payment channels yet. Click &quot;Add Channel&quot; to create one.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {paymentChannels.map(channel => (
                  <Card key={channel.id} className="p-6 border border-slate-200" data-testid={`channel-card-${channel.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-barlow-condensed text-xl font-bold text-primary">{channel.name}</h3>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${channel.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                          {channel.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm mb-4">
                      <p><span className="text-slate-500">Account Name:</span> <span className="font-semibold text-primary">{channel.account_name}</span></p>
                      <p><span className="text-slate-500">Number:</span> <span className="font-mono font-semibold text-primary">{channel.number}</span></p>
                      {channel.instructions && (
                        <p className="text-xs text-slate-600 mt-2 pt-2 border-t border-slate-100">{channel.instructions}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditChannel(channel)}
                        className="flex-1"
                        data-testid={`edit-channel-${channel.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteChannel(channel.id)}
                        data-testid={`delete-channel-${channel.id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}


        {activeTab === 'roster' && (
          <div>
            <h2 className="font-barlow-condensed text-3xl font-bold text-primary tracking-tight mb-2">Player Roster</h2>
            <p className="text-slate-600 mb-6">All registered children grouped by current age category</p>
            {playerRoster.length === 0 ? (
              <p className="text-slate-500">No players registered yet.</p>
            ) : (
              <div className="space-y-6">
                {playerRoster.map(group => (
                  <Card key={group.category} className="border border-slate-200 overflow-hidden" data-testid={`roster-group-${group.category}`}>
                    <div className="bg-primary text-white px-6 py-4 flex items-center justify-between">
                      <h3 className="font-barlow-condensed text-2xl font-bold tracking-tight">{group.category}</h3>
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
                        {group.count} {group.count === 1 ? 'player' : 'players'}
                      </span>
                    </div>
                    {/* Mobile card list */}
                    <div className="md:hidden divide-y divide-slate-100">
                      {group.players.map(player => {
                        const status = player.membership_status || 'Non-Member';
                        const badgeStyles = {
                          'Regular Member': 'border-emerald-300 bg-emerald-50 text-emerald-700',
                          'Affiliate Member': 'border-blue-300 bg-blue-50 text-blue-700',
                          'Special Member': 'border-yellow-300 bg-yellow-50 text-yellow-800',
                          'Non-Member': 'border-red-300 bg-red-50 text-red-700',
                        };
                        const badgeClass = badgeStyles[status] || badgeStyles['Non-Member'];
                        return (
                          <div key={player.id} className="p-4 bg-white" data-testid={`roster-player-mobile-${player.id}`}>
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-primary text-base">{player.name}</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {player.age !== undefined ? `${player.age} yrs` : '—'}
                                  {player.birthdate ? ` • Born ${new Date(player.birthdate).toLocaleDateString()}` : ''}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeletePlayer(player)}
                                data-testid={`delete-player-mobile-${player.id}`}
                                className="border-red-300 text-red-700 hover:bg-red-50 shrink-0 h-8 w-8 p-0"
                                aria-label={`Delete player ${player.name}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="text-sm text-slate-600 space-y-0.5 mb-3">
                              <p><span className="text-slate-500">Parent:</span> {player.parent_name}</p>
                              <p className="break-all"><span className="text-slate-500">Contact:</span> {player.parent_email}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Membership</p>
                              <Select
                                value={status}
                                onValueChange={(value) => handleUpdateMembership(player, value)}
                              >
                                <SelectTrigger
                                  className={`h-9 w-full text-xs font-semibold ${badgeClass}`}
                                  data-testid={`membership-select-mobile-${player.id}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Regular Member">Regular Member</SelectItem>
                                  <SelectItem value="Affiliate Member">Affiliate Member</SelectItem>
                                  <SelectItem value="Special Member">Special Member</SelectItem>
                                  <SelectItem value="Non-Member">Non-Member</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Desktop table */}
                    <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Player Name</TableHead>
                          <TableHead>Age</TableHead>
                          <TableHead>Birthdate</TableHead>
                          <TableHead>Parent</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Membership</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.players.map(player => {
                          const status = player.membership_status || 'Non-Member';
                          const badgeStyles = {
                            'Regular Member': 'border-emerald-300 bg-emerald-50 text-emerald-700',
                            'Affiliate Member': 'border-blue-300 bg-blue-50 text-blue-700',
                            'Special Member': 'border-yellow-300 bg-yellow-50 text-yellow-800',
                            'Non-Member': 'border-red-300 bg-red-50 text-red-700',
                          };
                          const badgeClass = badgeStyles[status] || badgeStyles['Non-Member'];
                          return (
                            <TableRow key={player.id} data-testid={`roster-player-${player.id}`}>
                              <TableCell className="font-medium">{player.name}</TableCell>
                              <TableCell>{player.age !== undefined ? `${player.age} yrs` : '—'}</TableCell>
                              <TableCell className="text-sm">
                                {player.birthdate ? new Date(player.birthdate).toLocaleDateString() : '—'}
                              </TableCell>
                              <TableCell>{player.parent_name}</TableCell>
                              <TableCell className="text-sm text-slate-600">{player.parent_email}</TableCell>
                              <TableCell>
                                <Select
                                  value={status}
                                  onValueChange={(value) => handleUpdateMembership(player, value)}
                                >
                                  <SelectTrigger
                                    className={`h-8 w-[170px] text-xs font-semibold ${badgeClass}`}
                                    data-testid={`membership-select-${player.id}`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Regular Member">Regular Member</SelectItem>
                                    <SelectItem value="Affiliate Member">Affiliate Member</SelectItem>
                                    <SelectItem value="Special Member">Special Member</SelectItem>
                                    <SelectItem value="Non-Member">Non-Member</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeletePlayer(player)}
                                  data-testid={`delete-player-${player.id}`}
                                  className="border-red-300 text-red-700 hover:bg-red-50"
                                  aria-label={`Delete player ${player.name}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'training-payments' && (
          <div>
            <h2 className="font-barlow-condensed text-3xl font-bold text-primary tracking-tight mb-2">Training Check-ins</h2>
            <p className="text-slate-600 mb-6">Grouped by training session, subcategorized by age group</p>
            {trainingPayments.length === 0 ? (
              <p className="text-slate-500">No training check-ins yet.</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-slate-600">
                    {trainingPaymentParts.upcoming.length} upcoming
                    {trainingPaymentParts.past.length > 0 && <span className="text-slate-400"> • {trainingPaymentParts.past.length} past</span>}
                  </p>
                  {trainingPaymentParts.past.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => togglePast('trainingPayments')}
                      data-testid="toggle-past-training-payments"
                    >
                      {showPast.trainingPayments ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      {showPast.trainingPayments ? 'Hide Past' : `Show Past (${trainingPaymentParts.past.length})`}
                    </Button>
                  )}
                </div>
                <div className="space-y-4">
                  {(showPast.trainingPayments ? [...trainingPaymentParts.upcoming, ...trainingPaymentParts.past] : trainingPaymentParts.upcoming).map((sessionGroup, sIdx) => {
                  const groupKey = `training_${sessionGroup.session_id}`;
                  const isExpanded = expandedGroups[groupKey] === true;
                  const isPast = sessionGroup.session_date && new Date(sessionGroup.session_date) < new Date(new Date().setHours(0,0,0,0));
                  return (
                    <Card key={groupKey} className={`border border-slate-200 overflow-hidden ${isPast ? 'opacity-80' : ''}`} data-testid={`training-group-${sIdx}`}>
                      <button
                        onClick={() => toggleGroup(groupKey)}
                        className="w-full p-4 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition-colors"
                        data-testid={`toggle-training-group-${sessionGroup.session_id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {isExpanded ? <ChevronDown className="h-5 w-5 text-primary shrink-0" /> : <ChevronRight className="h-5 w-5 text-primary shrink-0" />}
                          <div className="text-left min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-barlow-condensed text-lg font-bold text-primary truncate">
                                {sessionGroup.session_title || 'Training Session'}
                              </p>
                              {isPast && <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-semibold">Past</span>}
                            </div>
                            <p className="text-sm text-slate-600">
                              {sessionGroup.session_date ? new Date(sessionGroup.session_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm shrink-0">
                          <span className="hidden sm:inline text-slate-600">Verified: <span className="font-semibold text-emerald-700">₱{Number(sessionGroup.verified_total || 0).toLocaleString()}</span></span>
                          <span className="text-slate-600 hidden sm:inline">Total: <span className="font-semibold">{sessionGroup.count}</span></span>
                          {sessionGroup.pending_count > 0 && (
                            <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-semibold text-xs">
                              {sessionGroup.pending_count} Pending
                            </span>
                          )}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="p-4 space-y-6">
                          {sessionGroup.subgroups.map(sub => (
                            <div key={`${sessionGroup.session_id}_${sub.category}`} data-testid={`training-subgroup-${sessionGroup.session_id}-${sub.category}`}>
                              <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
                                <div className="flex items-center gap-2">
                                  <span className="inline-block px-2 py-1 rounded-full bg-primary text-white text-xs font-bold">{sub.category}</span>
                                  <span className="text-sm text-slate-600">{sub.count} {sub.count === 1 ? 'player' : 'players'}</span>
                                </div>
                                {sub.pending_count > 0 && (
                                  <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-semibold text-xs">
                                    {sub.pending_count} Pending
                                  </span>
                                )}
                              </div>
                              {/* Mobile card list */}
                              <div className="space-y-3 md:hidden">
                                {sub.check_ins.map(checkIn => {
                                  const attendance = checkIn.attendance || '';
                                  return (
                                    <div key={checkIn.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`training-payment-mobile-${checkIn.id}`}>
                                      <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="min-w-0 flex-1">
                                          <p className="font-semibold text-primary">{checkIn.player_name}</p>
                                        </div>
                                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold shrink-0 ${
                                          checkIn.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                          {checkIn.payment_status}
                                        </span>
                                      </div>
                                      <div className="text-sm text-slate-700 mb-3">
                                        <span className="font-semibold">₱{checkIn.fee_amount}</span>
                                        <span className="text-slate-500 ml-2">
                                          {checkIn.gcash_receipt_path ? (
                                            <button className="text-secondary hover:underline" onClick={() => viewReceipt(checkIn.gcash_receipt_path)} data-testid={`view-training-receipt-mobile-${checkIn.id}`}>View receipt</button>
                                          ) : 'No receipt'}
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap gap-2 items-center mb-2">
                                        {checkIn.payment_status === 'pending' && (
                                          <Button
                                            size="sm"
                                            className="bg-green-600 hover:bg-green-700 h-8"
                                            onClick={() => handleVerifyPayment(checkIn.id, 'check_in')}
                                            data-testid={`verify-training-mobile-${checkIn.id}`}
                                          >
                                            <CheckCircle className="h-4 w-4 mr-1" />
                                            Verify
                                          </Button>
                                        )}
                                        {attendance === 'Present' && (
                                          <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">Present</span>
                                        )}
                                        {attendance === 'No Show' && (
                                          <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">No Show</span>
                                        )}
                                        <Select
                                          value={attendance || 'none'}
                                          onValueChange={(v) => handleUpdateAttendance(checkIn, v === 'none' ? '' : v)}
                                        >
                                          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`attendance-select-mobile-${checkIn.id}`}>
                                            <SelectValue placeholder="Attendance" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="none">— Attendance</SelectItem>
                                            <SelectItem value="Present">Present</SelectItem>
                                            <SelectItem value="No Show">No Show</SelectItem>
                                          </SelectContent>
                                        </Select>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-8 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                                          onClick={() => handleWithdrawRegistrant({ id: checkIn.id, kind: 'training', player_name: checkIn.player_name, fee_amount: checkIn.fee_amount })}
                                          data-testid={`withdraw-training-mobile-${checkIn.id}`}
                                        >
                                          <UserMinus className="h-4 w-4 mr-1" />
                                          Withdraw
                                        </Button>
                                      </div>
                                      <AdminPaymentNoteWidget payment={checkIn} kind="training" onSaveNote={handleAdminSaveNote} onUploadReceipt={handleAdminUploadReceipt} />
                                    </div>
                                  );
                                })}
                              </div>
                              {/* Desktop table */}
                              <div className="hidden md:block overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Player</TableHead>
                                      <TableHead>Amount</TableHead>
                                      <TableHead>Status</TableHead>
                                      <TableHead>Receipt</TableHead>
                                      <TableHead>Actions</TableHead>
                                      <TableHead>Attendance Check</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {sub.check_ins.map(checkIn => {
                                      const attendance = checkIn.attendance || '';
                                      return (
                                      <React.Fragment key={checkIn.id}>
                                      <TableRow data-testid={`training-payment-${checkIn.id}`}>
                                        <TableCell className="font-medium">{checkIn.player_name}</TableCell>
                                        <TableCell>₱{checkIn.fee_amount}</TableCell>
                                        <TableCell>
                                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                                            checkIn.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                          }`}>
                                            {checkIn.payment_status}
                                          </span>
                                        </TableCell>
                                        <TableCell>
                                          {checkIn.gcash_receipt_path ? (
                                            <Button size="sm" variant="outline" onClick={() => viewReceipt(checkIn.gcash_receipt_path)} data-testid={`view-training-receipt-${checkIn.id}`}>
                                              View
                                            </Button>
                                          ) : (
                                            <span className="text-xs text-slate-400">No receipt</span>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {checkIn.payment_status === 'pending' && (
                                            <Button
                                              size="sm"
                                              className="bg-green-600 hover:bg-green-700"
                                              onClick={() => handleVerifyPayment(checkIn.id, 'check_in')}
                                              data-testid={`verify-training-${checkIn.id}`}
                                            >
                                              <CheckCircle className="h-4 w-4 mr-1" />
                                              Verify
                                            </Button>
                                          )}
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className={`border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 ${checkIn.payment_status === 'pending' ? 'ml-2' : ''}`}
                                            onClick={() => handleWithdrawRegistrant({ id: checkIn.id, kind: 'training', player_name: checkIn.player_name, fee_amount: checkIn.fee_amount })}
                                            data-testid={`withdraw-training-${checkIn.id}`}
                                          >
                                            <UserMinus className="h-4 w-4 mr-1" />
                                            Withdraw
                                          </Button>
                                        </TableCell>
                                        <TableCell>
                                          <div className="flex items-center gap-2">
                                            {attendance === 'Present' && (
                                              <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700" data-testid={`attendance-badge-${checkIn.id}`}>Present</span>
                                            )}
                                            {attendance === 'No Show' && (
                                              <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700" data-testid={`attendance-badge-${checkIn.id}`}>No Show</span>
                                            )}
                                            <Select
                                              value={attendance || 'none'}
                                              onValueChange={(v) => handleUpdateAttendance(checkIn, v === 'none' ? '' : v)}
                                            >
                                              <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`attendance-select-${checkIn.id}`}>
                                                <SelectValue placeholder="—" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="none">—</SelectItem>
                                                <SelectItem value="Present">Present</SelectItem>
                                                <SelectItem value="No Show">No Show</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                      <TableRow data-testid={`training-payment-note-row-${checkIn.id}`} className="bg-slate-50/50">
                                        <TableCell colSpan={6} className="py-2">
                                          <AdminPaymentNoteWidget payment={checkIn} kind="training" onSaveNote={handleAdminSaveNote} onUploadReceipt={handleAdminUploadReceipt} />
                                        </TableCell>
                                      </TableRow>
                                      </React.Fragment>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  );
                })}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'tournament-payments' && (
          <div>
            <h2 className="font-barlow-condensed text-3xl font-bold text-primary tracking-tight mb-2">Tournament Payments</h2>
            <p className="text-slate-600 mb-6">Grouped by tournament</p>
            {tournamentPayments.length === 0 ? (
              <p className="text-slate-500">No tournament payments yet.</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-slate-600">
                    {tournamentPaymentParts.upcoming.length} upcoming
                    {tournamentPaymentParts.past.length > 0 && <span className="text-slate-400"> • {tournamentPaymentParts.past.length} past</span>}
                  </p>
                  {tournamentPaymentParts.past.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => togglePast('tournamentPayments')}
                      data-testid="toggle-past-tournament-payments"
                    >
                      {showPast.tournamentPayments ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      {showPast.tournamentPayments ? 'Hide Past' : `Show Past (${tournamentPaymentParts.past.length})`}
                    </Button>
                  )}
                </div>
                <div className="space-y-4">
                  {(showPast.tournamentPayments ? [...tournamentPaymentParts.upcoming, ...tournamentPaymentParts.past] : tournamentPaymentParts.upcoming).map(group => {
                  const groupKey = `tournament_${group.tournament_id}`;
                  const isExpanded = expandedGroups[groupKey] === true;
                  const isPast = group.start_date && new Date(group.start_date) < new Date(new Date().setHours(0,0,0,0));
                  return (
                    <Card key={groupKey} className={`border border-slate-200 overflow-hidden ${isPast ? 'opacity-80' : ''}`} data-testid={`tournament-group-${group.tournament_id}`}>
                      <button
                        onClick={() => toggleGroup(groupKey)}
                        className="w-full p-4 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition-colors"
                        data-testid={`toggle-tournament-group-${group.tournament_id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {isExpanded ? <ChevronDown className="h-5 w-5 text-primary shrink-0" /> : <ChevronRight className="h-5 w-5 text-primary shrink-0" />}
                          <div className="text-left min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-barlow-condensed text-lg font-bold text-primary truncate">{group.tournament_title}</p>
                              {isPast && <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-semibold">Past</span>}
                            </div>
                            <p className="text-sm text-slate-600">{group.count} {group.count === 1 ? 'registration' : 'registrations'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm shrink-0">
                          <span className="hidden sm:inline text-slate-600">Verified: <span className="font-semibold text-emerald-700">₱{Number(group.verified_total || 0).toLocaleString()}</span></span>
                          {group.pending_count > 0 && (
                            <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-semibold text-xs">
                              {group.pending_count} Pending
                            </span>
                          )}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="p-4">
                          {/* Mobile card list */}
                          <div className="space-y-3 md:hidden">
                            {group.payments.map(payment => (
                              <div key={payment.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`tournament-payment-mobile-${payment.id}`}>
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-primary">{payment.player_name}</p>
                                    <p className="text-xs text-slate-500">{payment.category} • {new Date(payment.created_at).toLocaleDateString()}</p>
                                  </div>
                                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold shrink-0 ${
                                    payment.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {payment.payment_status}
                                  </span>
                                </div>
                                <div className="text-sm text-slate-700 mb-3">
                                  <span className="font-semibold">₱{payment.fee_amount}</span>
                                  <span className="text-slate-500 ml-2">
                                    {payment.gcash_receipt_path ? (
                                      <button className="text-secondary hover:underline" onClick={() => viewReceipt(payment.gcash_receipt_path)} data-testid={`view-tournament-receipt-mobile-${payment.id}`}>View receipt</button>
                                    ) : 'No receipt'}
                                  </span>
                                </div>
                                {payment.payment_status === 'pending' && (
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 h-8 mb-2 mr-2"
                                    onClick={() => handleVerifyPayment(payment.id, 'tournament')}
                                    data-testid={`verify-tournament-mobile-${payment.id}`}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Verify
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 mb-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                                  onClick={() => handleWithdrawRegistrant({ id: payment.id, kind: 'tournament', player_name: payment.player_name, fee_amount: payment.fee_amount })}
                                  data-testid={`withdraw-tournament-mobile-${payment.id}`}
                                >
                                  <UserMinus className="h-4 w-4 mr-1" />
                                  Withdraw
                                </Button>
                                <AdminPaymentNoteWidget payment={payment} kind="tournament" onSaveNote={handleAdminSaveNote} onUploadReceipt={handleAdminUploadReceipt} />
                              </div>
                            ))}
                          </div>
                          {/* Desktop table */}
                          <div className="hidden md:block overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Player</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {group.payments.map(payment => (
                                <React.Fragment key={payment.id}>
                                <TableRow data-testid={`tournament-payment-${payment.id}`}>
                                  <TableCell className="font-medium">{payment.player_name}</TableCell>
                                  <TableCell>{payment.category}</TableCell>
                                  <TableCell>₱{payment.fee_amount}</TableCell>
                                  <TableCell>
                                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                                      payment.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {payment.payment_status}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-sm">{new Date(payment.created_at).toLocaleDateString()}</TableCell>
                                  <TableCell>
                                    <div className="flex gap-2">
                                      {payment.gcash_receipt_path ? (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => viewReceipt(payment.gcash_receipt_path)}
                                          data-testid={`view-tournament-receipt-${payment.id}`}
                                        >
                                          View Receipt
                                        </Button>
                                      ) : (
                                        <span className="text-xs text-slate-400">No receipt</span>
                                      )}
                                      {payment.payment_status === 'pending' && (
                                        <Button
                                          size="sm"
                                          className="bg-green-600 hover:bg-green-700"
                                          onClick={() => handleVerifyPayment(payment.id, 'tournament')}
                                          data-testid={`verify-tournament-${payment.id}`}
                                        >
                                          <CheckCircle className="h-4 w-4 mr-1" />
                                          Verify
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                                        onClick={() => handleWithdrawRegistrant({ id: payment.id, kind: 'tournament', player_name: payment.player_name, fee_amount: payment.fee_amount })}
                                        data-testid={`withdraw-tournament-${payment.id}`}
                                      >
                                        <UserMinus className="h-4 w-4 mr-1" />
                                        Withdraw
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                                <TableRow data-testid={`tournament-payment-note-row-${payment.id}`} className="bg-slate-50/50">
                                  <TableCell colSpan={6} className="py-2">
                                    <AdminPaymentNoteWidget payment={payment} kind="tournament" onSaveNote={handleAdminSaveNote} onUploadReceipt={handleAdminUploadReceipt} />
                                  </TableCell>
                                </TableRow>
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {/* Training Session Roster Modal */}
      <Dialog open={showSessionRosterModal} onOpenChange={setShowSessionRosterModal}>
        <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 flex-shrink-0">
            <DialogTitle>Roster: {selectedSession?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
            {selectedSession && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-sm space-y-1">
                <p><span className="font-medium">Location:</span> {selectedSession.location}</p>
                <p><span className="font-medium">Date &amp; Time:</span> {new Date(selectedSession.date_time).toLocaleString()}</p>
                <p><span className="font-medium">Fee:</span> {selectedSession.fee_type === 'standard' ? '₱100 Standard' : '₱250 Special'}</p>
                <p><span className="font-medium">Check-ins:</span> {sessionRoster.length}</p>
              </div>
            )}
            {sessionRoster.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No check-ins yet.</p>
            ) : (
              <>
                {/* Mobile card list */}
                <div className="space-y-3 md:hidden">
                  {sessionRoster.map(entry => (
                    <div key={entry.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`session-roster-entry-mobile-${entry.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-semibold text-primary">{entry.player_name}</p>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${entry.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {entry.payment_status}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 space-y-0.5 mb-3">
                        <p><span className="text-slate-500">Parent:</span> {entry.parent_name}</p>
                        <p className="break-all"><span className="text-slate-500">Contact:</span> {entry.parent_email}</p>
                        <p><span className="text-slate-500">Receipt:</span> {entry.has_receipt ? <span className="text-green-700 font-semibold">✓ Uploaded</span> : <span className="text-slate-400">—</span>}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full h-8 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleWithdrawRegistrant({ id: entry.id, kind: 'training', player_name: entry.player_name, fee_amount: entry.fee_amount })}
                        data-testid={`withdraw-session-roster-mobile-${entry.id}`}
                      >
                        <UserMinus className="h-4 w-4 mr-1" />
                        Withdraw
                      </Button>
                    </div>
                  ))}
                </div>
                {/* Desktop table */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Player</TableHead>
                        <TableHead>Parent</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Receipt</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessionRoster.map(entry => (
                        <TableRow key={entry.id} data-testid={`session-roster-entry-${entry.id}`}>
                          <TableCell className="font-medium">{entry.player_name}</TableCell>
                          <TableCell>{entry.parent_name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{entry.parent_email}</TableCell>
                          <TableCell>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${entry.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {entry.payment_status}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm">
                            {entry.has_receipt ? (
                              <span className="text-green-700 font-semibold">✓</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                              onClick={() => handleWithdrawRegistrant({ id: entry.id, kind: 'training', player_name: entry.player_name, fee_amount: entry.fee_amount })}
                              data-testid={`withdraw-session-roster-${entry.id}`}
                            >
                              <UserMinus className="h-4 w-4 mr-1" />
                              Withdraw
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Tournament Roster Modal */}
      <Dialog open={showRosterModal} onOpenChange={setShowRosterModal}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 flex-shrink-0">
            <DialogTitle>Roster: {selectedTournament?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
            <p className="text-sm text-slate-600 mb-2">
              {tournamentRoster.length} of {selectedTournament?.participant_limit} spots filled
            </p>
            {selectedTournament && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-sm space-y-1">
                <p><span className="font-medium">Location:</span> {selectedTournament.location || 'TBA'}</p>
                <p><span className="font-medium">Date &amp; Time:</span> {new Date(selectedTournament.start_date).toLocaleString()}</p>
                <p><span className="font-medium">Category:</span> {selectedTournament.category}</p>
              </div>
            )}
            {tournamentRoster.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No registrations yet.</p>
            ) : (
              <>
                {/* Mobile card list */}
                <div className="space-y-3 md:hidden">
                  {tournamentRoster.map(reg => (
                    <div key={reg.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`roster-entry-mobile-${reg.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-semibold text-primary">{reg.player_name}</p>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${reg.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {reg.payment_status}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 space-y-0.5 mb-3">
                        <p><span className="text-slate-500">Category:</span> {reg.category}</p>
                        <p><span className="text-slate-500">Registered:</span> {new Date(reg.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full h-8 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleWithdrawRegistrant({ id: reg.id, kind: 'tournament', player_name: reg.player_name, fee_amount: reg.fee_amount })}
                        data-testid={`withdraw-tournament-roster-mobile-${reg.id}`}
                      >
                        <UserMinus className="h-4 w-4 mr-1" />
                        Withdraw
                      </Button>
                    </div>
                  ))}
                </div>
                {/* Desktop table */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Player</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Registered</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tournamentRoster.map(reg => (
                        <TableRow key={reg.id} data-testid={`roster-entry-${reg.id}`}>
                          <TableCell className="font-medium">{reg.player_name}</TableCell>
                          <TableCell>{reg.category}</TableCell>
                          <TableCell>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                              reg.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {reg.payment_status}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm">{new Date(reg.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                              onClick={() => handleWithdrawRegistrant({ id: reg.id, kind: 'tournament', player_name: reg.player_name, fee_amount: reg.fee_amount })}
                              data-testid={`withdraw-tournament-roster-${reg.id}`}
                            >
                              <UserMinus className="h-4 w-4 mr-1" />
                              Withdraw
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 flex-shrink-0">
            <DialogTitle>GCash Receipt</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 flex items-start justify-center bg-slate-50">
            {selectedReceipt && (
              <img
                src={selectedReceipt}
                alt="Receipt"
                className="max-w-full h-auto rounded-lg shadow-sm"
                data-testid="receipt-image"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Check-In / Registration Dialog */}
      <Dialog
        open={manualDialog.open}
        onOpenChange={(open) => { if (!open) setManualDialog({ open: false, type: null, target: null }); }}
      >
        <DialogContent className="w-[95vw] sm:max-w-md p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 flex-shrink-0">
            <DialogTitle>
              {manualDialog.type === 'training' ? 'Manual Check-In' : 'Manual Tournament Register'}
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <p className="text-sm text-slate-600">
              {manualDialog.type === 'training'
                ? <>Check a player into <span className="font-semibold text-primary">{manualDialog.target?.title}</span>. The unpaid balance will appear on the parent&apos;s account.</>
                : <>Register a player for <span className="font-semibold text-primary">{manualDialog.target?.title}</span>. The unpaid fee (₱{Number(manualDialog.target?.fee || 0).toLocaleString()}) will appear on the parent&apos;s account.</>
              }
            </p>
            <div>
              <Label htmlFor="manual-player-select">Choose player</Label>
              <Select value={manualPlayerId} onValueChange={setManualPlayerId}>
                <SelectTrigger id="manual-player-select" className="mt-1" data-testid="manual-player-select">
                  <SelectValue placeholder="Select a player" />
                </SelectTrigger>
                <SelectContent>
                  {allPlayersFlat.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · {p.group_label} · {p.parent_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setManualDialog({ open: false, type: null, target: null })} data-testid="manual-cancel">
                Cancel
              </Button>
              <Button
                onClick={submitManualAction}
                className="bg-primary text-white hover:bg-primary/90"
                disabled={!manualPlayerId}
                data-testid="manual-submit"
              >
                {manualDialog.type === 'training' ? 'Check-In Player' : 'Register Player'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
